import { useState, useEffect, useMemo, useRef } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { useAppSelector, useAppDispatch } from "@/app/hooks";
import {
  selectApplications,
  fetchApplications,
  selectApplicationData,
  selectApplicationLoading,
  fetchApplicationById,
  setApplicationData,
  setHasMultipleActiveApplications,
  setHasSelectedApplication,
} from "@/features/applicant/applicationSlice";
import { selectUser, updateUser } from "@/features/auth/authSlice";
import * as applicationService from "@/lib/api/applicationService";
import { overrideApplicantStatus } from "@/features/application/services/applicationService";
import { verifyUser } from "@/lib/api/userService";
import { EyeOff } from "lucide-react";
import { ApplicantApplicationsTable } from "@/features/applicant/components/ApplicantApplicationsTable";
import {
  mapApplicationToTableRow,
  partitionApplicationTableRows,
  sortApplicationTableRows,
} from "@/features/applicant/utils/applicantDashboardRows";
import type { ApplicationTableRow } from "@/features/applicant/types/utils";
import { DenialReasonsModal } from "@/features/application/components/DenialReasonsModal";
import { ConfirmDialog } from "@/components/ui/ConfirmDialog";
import { ValidationModal } from "@/components/ui/ValidationModal";
import {
  applicantConfirmDialogProps,
  applicantValidationModalProps,
} from "@/features/applicant/constants/applicantDialogProps";
import { endAdminImpersonation } from "@/lib/auth/endAdminImpersonation";
import { formatApiError, showWarning } from "@/lib/utils/notifications";
import { getTabPathWithApplicantId } from "@lib/utils/applicationNavigation";
import type { ResidentApplicationData } from "@/types/application";
import { buildSelectionConfirmHtml } from "@/features/applicant/utils/selectionConfirm";
import { useSystemAnnouncementPopup } from "@/features/system-announcement";

export function ApplicantDashboard() {
  const navigate = useNavigate();
  const dispatch = useAppDispatch();
  const applications = useAppSelector(selectApplications);
  const applicationData = useAppSelector(selectApplicationData);
  const isLoading = useAppSelector(selectApplicationLoading);
  const authUser = useAppSelector(selectUser);
  const { modal: systemAnnouncementModal } = useSystemAnnouncementPopup();

  const [applicationsLoading, setApplicationsLoading] = useState(false);
  const [sortColumn, setSortColumn] = useState<string | null>(
    "lease_start_date",
  );
  const [sortOrder, setSortOrder] = useState<"asc" | "desc">("desc");
  const [showConfirmDialog, setShowConfirmDialog] = useState(false);
  const [selectedApp, setSelectedApp] = useState<ApplicationTableRow | null>(
    null,
  );
  // Track which row is loading (by unique key) so only that button shows spinner; others are disabled
  const [loadingRowId, setLoadingRowId] = useState<string | number | null>(
    null,
  );
  const [updateInviteError, setUpdateInviteError] = useState<string | null>(
    null,
  );
  const [searchParams] = useSearchParams();
  const [isAdminImpersonating, setIsAdminImpersonating] = useState(false);
  const [denialModalOpen, setDenialModalOpen] = useState(false);
  const [selectedApplicantId, setSelectedApplicantId] = useState<
    number | string | null
  >(null);
  // Validation Message popup – show only when user clicks Continue on a Property Approved row (no banner/toast on load)
  const [showPropertyApprovedApplicantPopup, setShowPropertyApprovedApplicantPopup] =
    useState(false);
  // When user clicks Continue on a Property Approved row, show popup first then proceed on Ok
  const [pendingContinueRow, setPendingContinueRow] = useState<ApplicationTableRow | null>(null);

  // Check if admin is impersonating
  useEffect(() => {
    const adminToken = localStorage.getItem("admin_token");
    const impersonateFlag = localStorage.getItem("impersonate");
    const urlImpersonate = searchParams.get("isImpersonate") === "true";
    setIsAdminImpersonating(!!adminToken || impersonateFlag === "true" || urlImpersonate);
  }, [searchParams]);

  // Do NOT show Validation Message as banner/toast on load. Show only when user clicks Continue on a Property Approved row.

  const [stopImpersonateBusy, setStopImpersonateBusy] = useState(false);

  // Handle stop impersonation — Angular `layout.endImpersonate`: DELETE /api/impersonate, verify, restore admin user
  const handleStopImpersonate = async () => {
    if (stopImpersonateBusy) return;
    setStopImpersonateBusy(true);
    try {
      const originalApplicantId = localStorage.getItem("impersonated_applicant_id");
      const redirect = await endAdminImpersonation({
        dispatch,
        applicantFallback: {
          originalApplicantId,
          applicationsListPath: "/application",
        },
      });
      window.location.href = redirect;
    } catch (e) {
      showWarning(formatApiError(e));
      setStopImpersonateBusy(false);
    }
  };

  const handleShowDenialReasons = (applicantId: string | number) => {
    setSelectedApplicantId(applicantId);
    setDenialModalOpen(true);
  };

  const handleCloseDenialModal = () => {
    setDenialModalOpen(false);
    setSelectedApplicantId(null);
  };

  const getRowKey = (row: ApplicationTableRow): string | number =>
    row.inviteId ?? row.applicantId ?? row.applicationId ?? "";
  const isAnyButtonLoading = loadingRowId !== null;
  const lastFetchParamsRef = useRef<{
    sortBy: string;
    sortOrder: string;
  } | null>(null);
  const isFetchingRef = useRef(false);

  // Fetch applications on mount and when sort changes
  useEffect(() => {
    const fetchApps = async () => {
      // Prevent duplicate concurrent fetches
      if (isFetchingRef.current) return;

      // Check if we've already fetched with these exact params
      const sortBy = sortColumn || "lease_start_date";
      const sortOrderParam = sortOrder || "desc";

      if (
        lastFetchParamsRef.current &&
        lastFetchParamsRef.current.sortBy === sortBy &&
        lastFetchParamsRef.current.sortOrder === sortOrderParam
      ) {
        // Already fetched with these params, skip
        return;
      }

      isFetchingRef.current = true;
      setApplicationsLoading(true);
      try {
        await dispatch(
          fetchApplications({ sortBy, sortOrder: sortOrderParam }),
        ).unwrap();
        lastFetchParamsRef.current = { sortBy, sortOrder: sortOrderParam };
      } catch (error) {
        showWarning(formatApiError(error));
      } finally {
        setApplicationsLoading(false);
        isFetchingRef.current = false;
      }
    };
    fetchApps();
  }, [dispatch, sortColumn, sortOrder]);

  // Note: Auto-navigation is handled in the login flow, not here
  // This ensures that when user clicks "Home", they stay on the dashboard
  // Auto-navigation only happens after login when navigating to dashboard for the first time

  const tableRows = useMemo(
    () => applications.map(mapApplicationToTableRow),
    [applications],
  );

  const { applications: applicationRows } =
    useMemo(() => partitionApplicationTableRows(tableRows), [tableRows]);

  const sortedApplicationRows = useMemo(
    () => sortApplicationTableRows(applicationRows, sortColumn, sortOrder),
    [applicationRows, sortColumn, sortOrder],
  );

  // Active standard applications only
  const activeApplications = useMemo(() => {
    return applicationRows.filter((row) => row.active === true);
  }, [applicationRows]);

  // Update Redux state when active applications count changes
  // This is used by ApplicationRouteGuard and ApplicantSidebar to control access
  // Matching Angular behavior: when multiple active apps exist, user must select one from dashboard
  // After selection, backend cancels other active apps, leaving only one active
  useEffect(() => {
    const hasMultiple = activeApplications.length > 1;
    dispatch(setHasMultipleActiveApplications(hasMultiple));

    // If only one active application (or zero), user doesn't need to select - mark as selected
    // This allows navigation to work for single active application
    // Also handles the case after user selects and other apps are cancelled
    if (activeApplications.length <= 1) {
      dispatch(setHasSelectedApplication(true));
    } else {
      // Multiple active apps - reset selection state so user must choose
      dispatch(setHasSelectedApplication(false));
    }
  }, [activeApplications.length, dispatch]);

  const handleSort = (column: string) => {
    if (sortColumn === column) {
      setSortOrder(sortOrder === "asc" ? "desc" : "asc");
    } else {
      setSortColumn(column);
      setSortOrder("asc");
    }
  };

  const isPropertyApprovedStatus = (status: string) =>
    status?.toUpperCase() === "PROPERTY_APPROVED" || status === "Property Approved";

  // Handle Start New or Continue button click – only on this click we update selected invite and navigate
  // For Property Approved: show Validation Message popup first; on Ok proceed with navigation
  const handleStartContinue = async (row: ApplicationTableRow) => {
    if (isPropertyApprovedStatus(row.status)) {
      setPendingContinueRow(row);
      setShowPropertyApprovedApplicantPopup(true);
      return;
    }
    if (activeApplications.length > 1) {
      setSelectedApp(row);
      setShowConfirmDialog(true);
      return;
    }
    // Single active: still call update_user_invite so backend has selected invite, then navigate
    await selectInviteAndNavigate(row);
  };

  // Find the selected invite in the refreshed list (by invite_id, application_id, or applicant_id)
  const findSelectedAppInList = (
    list: ResidentApplicationData[],
    original: ResidentApplicationData,
  ) => {
    return list.find((a: ResidentApplicationData) => {
      if (
        original.invite_id != null &&
        a.invite_id != null &&
        Number(a.invite_id) === Number(original.invite_id)
      )
        return true;
      const appId = original.fk_application_id ?? original.application_id;
      if (
        appId != null &&
        (a.fk_application_id ?? a.application_id) != null &&
        Number(a.fk_application_id ?? a.application_id) === Number(appId)
      )
        return true;
      if (
        original.applicant_id != null &&
        a.applicant_id != null &&
        Number(a.applicant_id) === Number(original.applicant_id)
      )
        return true;
      return false;
    });
  };

  // Update selected invite (update_user_invite), refresh user/list, then navigate to About using ID from refreshed list.
  const selectInviteAndNavigate = async (
    row: ApplicationTableRow,
    errorFallback = "Failed to open application. Please try again.",
  ): Promise<boolean> => {
    setUpdateInviteError(null);
    setLoadingRowId(getRowKey(row));
    try {
      await applicationService.updateUserInvite(row.originalApp);
      const userData = await verifyUser(true);
      dispatch(updateUser(userData));
      const refreshedList = await dispatch(
        fetchApplications({
          sortBy: sortColumn || "lease_start_date",
          sortOrder,
        }),
      ).unwrap();
      const selected =
        findSelectedAppInList(refreshedList, row.originalApp) ??
        row.originalApp;
      dispatch(setApplicationData(selected));
      const applicantIdToUse =
        selected?.applicant_id != null && selected.applicant_id !== ""
          ? selected.applicant_id
          : row.applicantId;

      // Mark that user has explicitly selected an application
      // This allows navigation to application pages even when multiple were active
      dispatch(setHasSelectedApplication(true));

      if (applicantIdToUse) {
        try {
          await dispatch(fetchApplicationById(applicantIdToUse)).unwrap();
        } catch {
          // ResidentApplication will fetch by URL applicant id
        }
        navigate(getTabPathWithApplicantId("about", applicantIdToUse), {
          state: { tab: "about" },
        });
      } else {
        navigate(`/applicant/application/about`, { replace: true });
      }
      return true;
    } catch (error) {
      const message =
        (error as { message?: string })?.message || errorFallback;
      setUpdateInviteError(message);
      return false;
    } finally {
      setLoadingRowId(null);
    }
  };

  // Handle confirmation dialog: on confirm, same flow – update_user_invite, refresh, find selected in list, navigate to About
  const handleConfirmDialog = async (confirmed: boolean) => {
    if (!confirmed) {
      setShowConfirmDialog(false);
      setSelectedApp(null);
      return;
    }
    if (!selectedApp || isAnyButtonLoading) {
      if (!selectedApp) setShowConfirmDialog(false);
      return;
    }
    const row = selectedApp;
    const ok = await selectInviteAndNavigate(
      row,
      "Failed to update selected invite. Please try again.",
    );
    if (ok) {
      setShowConfirmDialog(false);
      setSelectedApp(null);
    }
  };

  const handleView = async (row: ApplicationTableRow) => {
    // For VIEW button: when impersonating and status is Property Approved, set status to INCOMPLETE then land on About yourself (match Angular)
    setLoadingRowId(getRowKey(row));
    // Mark that user has explicitly selected an application (matches Angular's routerLink behavior
    // where clicking View on the list always navigates to the application page)
    dispatch(setHasSelectedApplication(true));
    try {
      if (row.applicantId && isAdminImpersonating && isPropertyApprovedStatus(row.status)) {
        await overrideApplicantStatus(row.applicantId as string | number, {
          new_applicant_status: "INCOMPLETE",
          new_application_status: "INCOMPLETE",
          new_progress_status: "",
          send_mail: false,
          remark: "",
          lock_applicants: [],
        });
        await dispatch(fetchApplicationById(row.applicantId)).unwrap();
        navigate(getTabPathWithApplicantId("about", row.applicantId!), {
          state: { tab: "about" },
        });
      } else if (row.applicantId) {
        await dispatch(fetchApplicationById(row.applicantId)).unwrap();
        navigate(getTabPathWithApplicantId("about", row.applicantId!), {
          state: { tab: "about" },
        });
      } else if (row.applicationId) {
        dispatch(setApplicationData(row.originalApp));
        navigate(`/applicant/application/about`, { replace: true });
        setLoadingRowId(null);
      }
    } catch (err) {
      const message = (err as { message?: string })?.message || "Failed to open application.";
      showWarning(formatApiError(err));
      setUpdateInviteError(message);
    } finally {
      setLoadingRowId(null);
    }
  };

  // Get user name from application data or auth user - same logic as sidebar
  // Priority: applicationData > authUser > 'User'
  const userName = useMemo(() => {
    const firstName =
      applicationData?.first_name ||
      applicationData?.applicant_first_name ||
      authUser?.first_name ||
      "";
    const lastName =
      applicationData?.last_name ||
      applicationData?.applicant_last_name ||
      authUser?.last_name ||
      "";
    const name = `${firstName} ${lastName}`.trim();
    return name || "User";
  }, [
    applicationData?.first_name,
    applicationData?.applicant_first_name,
    applicationData?.last_name,
    applicationData?.applicant_last_name,
    authUser?.first_name,
    authUser?.last_name,
  ]);

  // Get button text and style based on activeButton
  const getButtonConfig = (row: ApplicationTableRow) => {
    const buttonType = row.activeButton || "CONTINUE";

    switch (buttonType) {
      case "START_NEW":
        return {
          text: "Start New",
          className:
            "px-4 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700 transition-all font-medium text-sm shadow-sm hover:shadow-md",
        };
      case "VIEW":
        return {
          text: "View",
          className:
            "px-4 py-2 border border-gray-300 rounded-lg text-gray-700 bg-white hover:bg-gray-50 hover:border-gray-400 transition-all font-medium text-sm shadow-sm hover:shadow",
        };
      case "CONTINUE":
      default:
        return {
          text: "Continue",
          className:
            "px-4 py-2 bg-[#1597ff] text-white rounded-lg hover:bg-[#0d7acc] transition-all font-medium text-sm shadow-sm hover:shadow-md",
        };
    }
  };

  return (
    <div data-testid="applicant-dashboard-page" className="p-3 sm:p-6 bg-gray-50 min-h-screen">
      {systemAnnouncementModal}
      {/* Welcome Section */}
      <div className="mb-6 sm:mb-8 flex flex-col sm:flex-row sm:items-start sm:justify-between gap-4">
        <div className="min-w-0">
          <p className="text-green-600 text-sm font-medium mb-1">Welcome,</p>
          <h1 className="text-2xl sm:text-4xl font-bold text-gray-900 truncate">{userName}</h1>
        </div>
        <div className="flex flex-col sm:flex-row items-stretch sm:items-center gap-2 sm:gap-3 shrink-0">
          {isAdminImpersonating && (
            <button
              type="button"
              onClick={() => void handleStopImpersonate()}
              disabled={stopImpersonateBusy}
              data-testid="applicant-dashboard-stop-impersonate-btn"
              className="flex items-center justify-center gap-2 px-3 py-1.5 bg-[#1597ff] text-white rounded-md hover:bg-[#0d7acc] transition-colors shadow-sm disabled:opacity-60"
            >
              <EyeOff className="w-4 h-4" />
              <span className="text-sm font-medium">
                {stopImpersonateBusy ? "Stopping…" : "Stop Impersonate"}
              </span>
            </button>
          )}
        </div>
      </div>

      {/* Multiple Active Applications Message */}
      {activeApplications.length > 1 && !applicationsLoading && !isLoading && (
        <div className="mb-6 bg-blue-50 border border-blue-200 rounded-lg p-4">
          <p className="text-sm text-blue-800 font-medium">
            We have found multiple active invites and applications, please
            select one of them to proceed.
          </p>
        </div>
      )}

      {/* Inline error when update invite fails (e.g. single active click) */}
      {updateInviteError && !showConfirmDialog && (
        <div className="mb-6 p-4 bg-red-50 border border-red-200 rounded-lg">
          <p className="text-sm text-red-800">{updateInviteError}</p>
        </div>
      )}

      <ApplicantApplicationsTable
        title="Primary Applications"
        testIdPrefix="applicant-dashboard"
        rows={sortedApplicationRows}
        isLoading={applicationsLoading}
        sortColumn={sortColumn}
        sortOrder={sortOrder}
        onSort={handleSort}
        loadingRowId={loadingRowId}
        isAnyButtonLoading={isAnyButtonLoading}
        emptyTitle="No applications found"
        emptySubtitle="Start by applying for a property"
        onStartContinue={handleStartContinue}
        onView={handleView}
        onShowDenialReasons={handleShowDenialReasons}
        getRowKey={getRowKey}
        getButtonConfig={getButtonConfig}
      />

      <ConfirmDialog
        {...applicantConfirmDialogProps}
        isOpen={showConfirmDialog && !!selectedApp}
        title="Confirmation"
        message={
          selectedApp
            ? [
                `You have selected "${selectedApp.propertyName}"${selectedApp.unitNumber ? ` with Unit Number "${selectedApp.unitNumber}"` : ""}.`,
                "Please confirm if you would like to go ahead with this selection.",
                "NOTE: Post confirmation, all your other active records will get auto cancelled and cannot be reversed!!",
                updateInviteError ? updateInviteError : "",
              ]
                .filter(Boolean)
                .join("\n\n")
            : ""
        }
        htmlMessage={
          selectedApp
            ? buildSelectionConfirmHtml(
                selectedApp.propertyName,
                selectedApp.unitNumber,
                updateInviteError,
              )
            : undefined
        }
        primaryLabel={isAnyButtonLoading ? "Updating..." : "Confirm"}
        onPrimary={() => {
          if (isAnyButtonLoading) return;
          void handleConfirmDialog(true);
        }}
        secondaryLabel="Cancel"
        onSecondary={() => handleConfirmDialog(false)}
        onDismiss={() => handleConfirmDialog(false)}
        overlayDataTestId="applicant-modal-select-confirm"
        primaryButtonDataTestId="applicant-modal-select-confirm-ok-btn"
        secondaryButtonDataTestId="applicant-modal-select-confirm-cancel-btn"
      />

      <ValidationModal
        {...applicantValidationModalProps}
        isOpen={showPropertyApprovedApplicantPopup}
        title="Validation Message"
        message="You have been identified as 'approved by property', so no Liberty Rent screening is needed. Your co-applicant(s) are currently completing their screening. Once they finish, the combined application can proceed."
        showValidationIcon={false}
        onClose={() => {
          if (pendingContinueRow) {
            const row = pendingContinueRow;
            setPendingContinueRow(null);
            if (activeApplications.length > 1) {
              setSelectedApp(row);
              setShowConfirmDialog(true);
            } else {
              void selectInviteAndNavigate(row);
            }
          }
          setShowPropertyApprovedApplicantPopup(false);
        }}
        overlayDataTestId="applicant-modal-property-approved-validation"
        confirmButtonDataTestId="applicant-modal-property-approved-validation-ok-button"
      />

      {/* Denial Reasons Modal */}
      <DenialReasonsModal
        applicantId={selectedApplicantId}
        isOpen={denialModalOpen}
        onClose={handleCloseDenialModal}
        denialEmailContentCallSite="residentApplicationList"
      />
    </div>
  );
}
